import React, { useState, useEffect } from "react";
import * as yup from "yup";
import { observer } from "mobx-react";
import { get } from "lodash";
import classnames from "classnames";
import accounting from "accounting";

import {
  Controller,
  useForm,
  useFieldArray,
  FormProvider,
  appendErrors,
} from "react-hook-form";
import { toast } from "react-toastify";
import Select2 from "react-select2-wrapper";
import {
  Row,
  Container,
  Col,
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  InputGroup,
  Table,
  InputGroupText,
  InputGroupAddon,
  Input,
} from "reactstrap";
import { useMst } from "store/RootStore";
import { yupResolver } from "@hookform/resolvers";
import { useHistory } from "react-router";

import {
  CreateRateInput,
  UserType,
  UsersResponse,
  FormTemplateHeadersResponse,
  FormTemplateHeaderType,
  RateType,
  BobScores,
} from "types";
import { AxiosResponse } from "axios";
import Loading from "components/Loading";
import Skeleton from "react-loading-skeleton";
import CreateRateGroup from "./components/CreateRateGroup";
import { convertFormToRate } from "utils/helpers";
import schema from "./schema";
import { cast, getSnapshot } from "mobx-state-tree";
import { Rate } from "store/models/Rate";
import config from "config";

const CreateRate: React.FC = observer(() => {
  const store = useMst();
  const history = useHistory();
  const [pageLoading, setPageLoading] = useState(false);
  const [bobScore, setBobScore] = useState<BobScores | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ratees, setRatees] = useState<UserType[]>([]);
  const [selectedRateeUser, setSelectedRateeUser] = useState<UserType | null>(
    null
  );
  const [errors, setErrors] = useState<any>({});
  const [rateesFormatted, setRateesFormatted] = useState<
    {
      id: number;
      text: string;
    }[]
  >([]);
  const [forms, setForms] = useState<FormTemplateHeaderType[]>([]);
  const [formsFormatted, setFormsFormatted] = useState<
    {
      id: number;
      text: string;
    }[]
  >([]);

  useEffect(() => {
    setPageLoading(true);
    Promise.all<AxiosResponse<UsersResponse>>([
      store.userAccount.getMyRateesUsers(),
    ])
      .then((resp) => {
        setRatees(resp[0].data.data);
      })
      .then(() => setPageLoading(false));
    store.resetCreateRate();
  }, []);

  useEffect(() => {
    const r = ratees.map((ratee) => ({
      id: ratee.id,
      text:
        ratee && ratee.profile
          ? `${ratee.profile.firstName} ${ratee.profile.lastName}`
          : "",
    }));
    setRateesFormatted(r);
  }, [ratees]);

  useEffect(() => {
    setPageLoading(true);
    store.userAccount
      .getUserForms(store.createRate.ratee)
      .then((resp) => resp.data.data)
      .then((data) => setForms(data))
      .catch((e) => console.log(e))
      .then(() => setPageLoading(false));
  }, [store.createRate.ratee]);

  useEffect(() => {
    const r = forms.map((form) => ({
      id: form.id,
      text: form.name,
    }));
    setFormsFormatted(r);
  }, [forms]);

  useEffect(() => {
    if (bobScore) {
      store.createRate.groups[0].setTotalScore(parseFloat(bobScore.total));
    }
  }, [bobScore]);

  const handleChangeFormTemplate = async (formTemplate: number) => {
    const form = forms.filter((f) => f.id == store.createRate.formTemplate)[0];
    if (form) {
      const r = convertFormToRate(form);
      store.createRate.setType(r.type);
      store.createRate.setName(r.name);
      store.createRate.setGroups(r.groups);

      // check if first is bob, then get predefined scores
      if (r.groups[0].type === config.formGroupTypes.BOB) {
        const response = await store.userAccount.getUserBobScores(
          selectedRateeUser?.id,
          form.bobVersion
        );

        const score = JSON.parse(response.data.score.score);
        setBobScore(score);
        console.log(score);

        const bobGroup = store.createRate.groups[0];
        for (let i = 0; i < bobGroup.items.length; i++) {
          const item = bobGroup.items[i];
          for (let si = 0; si < item.subitems.length; si++) {
            const subitem = item.subitems[si];
            const value = score.individual[i][si];
            subitem.setScore(value);
          }
        }
      }
    }
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();

    const data = getSnapshot(store.createRate);
    try {
      setIsSubmitting(true);
      await schema.validate(data, { abortEarly: false });

      store.createRate.computeTotalScores();

      const resp = await store.userAccount.saveRate(store.createRate);
      const rate = resp.data.data;
      toast.success("Successfully saved rate");

      if (store.createRate.type === config.formHeaderTypes.WITHOUT_IDP_PIP) {
        history.push("/admin/rates/list");
      } else {
        if (store.createRate.totalScore >= 3) {
          history.push(`/admin/rate_employee/${rate.uuid}/idp`);
        } else {
          history.push(`/admin/rate_employee/${rate.uuid}/pip`);
        }
      }
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      setIsSubmitting(false);
    } catch (e) {
      setIsSubmitting(false);

      console.log(errors);
      const newErrors: { [key: string]: string } = {};
      (e as yup.ValidationError).inner.forEach(function (er) {
        newErrors[er.path] = er.message;
      });

      setErrors(newErrors);
      toast.error("Sorry, failed to save form. Please complete all details");
    }
  };

  const saveData = () => {};

  console.log(store.createRate.groups);

  return (
    <>
      <div className="header bg-default pb-6">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-4">
              <Col lg="6" xs="7">
                <h6 className="h2 text-white d-inline-block mb-0">
                  Rate Employee
                </h6>
              </Col>
              <Col className="text-right" lg="6" xs="5"></Col>
            </Row>
          </div>
        </Container>
      </div>
      <form onSubmit={onSubmit}>
        <Container className="mt--6" fluid>
          <Row>
            <Col xl="12">
              <Card>
                <CardHeader>
                  <h5 className="h3 mb-0">Rate Details</h5>
                </CardHeader>
                {pageLoading ? (
                  <CardBody>
                    <Skeleton count={5} />
                  </CardBody>
                ) : (
                  <CardBody>
                    <FormGroup>
                      <label className="form-control-label">Year</label>
                      <InputGroup className={classnames("input-group-merge")}>
                        <select
                          className="form-control"
                          value={store.createRate.year}
                          onChange={(e: any) => {
                            store.createRate.setYear(e.target.value);
                          }}
                        >
                          <option value="2025">2025</option>
                          <option value="2024">2024</option>
                          <option value="2023">2023</option>
                          <option value="2022">2022</option>
                          <option value="2021">2021</option>
                          <option value="2020">2020</option>
                        </select>
                      </InputGroup>
                      {errors["year"] ? (
                        <span className="text-danger mt-3 d-block">
                          Please select year
                        </span>
                      ) : null}
                    </FormGroup>
                    <FormGroup>
                      <label className="form-control-label">Period</label>
                      <InputGroup className={classnames("input-group-merge")}>
                        <select
                          className="form-control"
                          value={store.createRate.period}
                          onChange={(e: any) => {
                            store.createRate.setPeriod(e.target.value);
                          }}
                        >
                          <option value="1">First</option>
                          <option value="2">Second</option>
                        </select>
                      </InputGroup>
                      {errors["period"] ? (
                        <span className="text-danger mt-3 d-block">
                          Please select a period
                        </span>
                      ) : null}
                    </FormGroup>
                    <FormGroup>
                      <label className="form-control-label">Ratee</label>
                      <InputGroup className={classnames("input-group-merge")}>
                        <select
                          className="form-control"
                          value={store.createRate.ratee}
                          onChange={(e: any) => {
                            const id = parseInt(e.target.value);
                            store.createRate.setRatee(parseInt(e.target.value));
                            const u = ratees.filter((r) => r.id === id)[0];
                            setSelectedRateeUser(u);
                          }}
                        >
                          <option>Please select a ratee</option>
                          {rateesFormatted.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.text}
                            </option>
                          ))}
                        </select>
                      </InputGroup>
                      {errors["ratee"] ? (
                        <span className="text-danger mt-3 d-block">
                          Please select a ratee
                        </span>
                      ) : null}
                    </FormGroup>
                    <FormGroup>
                      <label className="form-control-label">
                        Form Template
                      </label>
                      <InputGroup className={classnames("input-group-merge")}>
                        <select
                          className="form-control"
                          value={store.createRate.formTemplate}
                          onChange={(e: any) => {
                            const value = parseInt(e.target.value);
                            store.createRate.setFormTemplate(value);
                            handleChangeFormTemplate(value);
                          }}
                        >
                          <option>Please select a form</option>
                          {formsFormatted.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.text}
                            </option>
                          ))}
                        </select>
                      </InputGroup>
                      {errors["formTemplate"] ? (
                        <span className="text-danger mt-3 d-block">
                          Please select a form template
                        </span>
                      ) : null}
                    </FormGroup>
                  </CardBody>
                )}
              </Card>

              {store.createRate.groups.map((group, index) => {
                return (
                  <CreateRateGroup
                    key={index}
                    ratee={selectedRateeUser}
                    errors={errors}
                    group={group}
                    index={index}
                    bobScore={bobScore}
                    year={store.createRate.year}
                  />
                );
              })}
            </Col>

            {store.createRate && store.createRate.formTemplate > 0 ? (
              <Col xl="12" l="12">
                <Card>
                  <CardHeader>
                    <div className="d-flex justify-content-between">
                      <h5 className="h3 mb-0">OVERALL RATING</h5>
                      <h5>{store.createRate.rateDescription}</h5>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <Table responsive>
                      <tbody>
                        {store.createRate.groups.map((d) => {
                          return (
                            <tr key={d.name}>
                              <td>
                                {d.name} ({d.weight}%)
                              </td>
                              <td>{accounting.formatNumber(d.score, 2)}</td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td>OVERALL SCORE(100%)</td>
                          <td>
                            {accounting.formatNumber(
                              store.createRate.overallScore,
                              2
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              </Col>
            ) : null}

            <Col xl="12" l="12">
              <Card>
                <CardHeader>
                  <h5 className="h3 mb-0">Actions</h5>
                </CardHeader>

                <CardBody>
                  <Button
                    disabled={isSubmitting}
                    type="submit"
                    htmlType="submit"
                    className="btn-default mr-2"
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </form>
    </>
  );
});

export default CreateRate;
